import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import { Boxes, Check, ChevronsUpDown, Layers } from 'lucide-react'
import { useState } from 'react'
import { allApplicationsQuery } from '@/api/queries/applications'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/cn'
import { substringFilter } from '@/lib/search'
import { useAppScope } from './useAppScope'

/** Top-bar selector for the global application scope. */
export function AppScopeSelect() {
  const { appId, application, setAppId } = useAppScope()
  const [open, setOpen] = useState(false)
  const apps = useQuery({ ...allApplicationsQuery, enabled: open || !!appId })
  const label = appId ? (application?.name ?? appId) : 'All applications'

  const choose = (id: string | undefined) => {
    setAppId(id)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Application scope: ${label}`}
          className={cn(
            'flex h-8 max-w-60 min-w-0 cursor-pointer items-center gap-2 rounded-md border px-2.5 text-sm font-medium transition-colors',
            'focus-visible:outline-2 focus-visible:outline-focus',
            appId
              ? 'border-brass-line/60 bg-brass-soft text-ink hover:border-brass-line'
              : 'border-line-strong bg-surface text-ink-2 hover:border-ink-3 hover:text-ink',
          )}
        >
          {appId ? (
            <Boxes className="size-4 shrink-0 text-brass" />
          ) : (
            <Layers className="size-4 shrink-0 text-ink-3" />
          )}
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-ink-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command filter={substringFilter}>
          <Command.Input
            placeholder="Find an application…"
            className="h-9 w-full border-b border-line bg-transparent px-3 text-sm outline-none placeholder:text-ink-3"
          />
          <Command.List className="max-h-80 overflow-y-auto p-1">
            <Command.Empty className="px-2 py-3 text-sm text-ink-3">
              {apps.isError ? 'Couldn’t load applications' : 'No matching applications'}
            </Command.Empty>
            <ScopeItem
              value="__all__"
              label="All applications"
              hint="No filter"
              selected={!appId}
              onSelect={() => choose(undefined)}
            />
            {apps.isPending && <div className="px-2 py-2 text-sm text-ink-3">Loading…</div>}
            {apps.data && apps.data.length > 0 && (
              <Command.Group
                heading="Applications"
                className="mt-1 border-t border-line pt-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-ink-3"
              >
                {apps.data.map((a) => (
                  <ScopeItem
                    key={a.id}
                    value={a.id}
                    keywords={[a.name, a.slug]}
                    label={a.name}
                    hint={a.slug}
                    selected={appId === a.id}
                    muted={a.status === 'suspended'}
                    onSelect={() => choose(a.id)}
                  />
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function ScopeItem(props: {
  value: string
  label: string
  hint?: string
  keywords?: string[]
  selected: boolean
  muted?: boolean
  onSelect: () => void
}) {
  return (
    <Command.Item
      value={props.value}
      keywords={props.keywords ?? [props.label]}
      onSelect={props.onSelect}
      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-surface-3"
    >
      <Check className={cn('size-3.5 shrink-0 text-brass', !props.selected && 'invisible')} />
      <span className={cn('truncate', props.muted && 'text-ink-3')}>{props.label}</span>
      {props.hint && (
        <span className="ml-auto truncate pl-2 font-mono text-xs text-ink-3">{props.hint}</span>
      )}
    </Command.Item>
  )
}
